import { of, Observable } from 'rxjs';
import { Injectable } from '@angular/core'
import { sortBy } from 'lodash'

import { PublicLinkService } from '../../services/public-link/public-link.service'
import { BackendService } from '../backend/backend.service'
import { ObjectBaseService } from '../object-base/object-base.service'
import { SharedObjectBaseService } from '../shared-object-base/shared-object-base.service'
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { SnapshotSharingPermissions } from 'app/apps/snapshot/app/models/snapshot-sharing-permissions.model';

@Injectable({
  providedIn: 'root'
})
export class SharingService {
  constructor(
    private objectBaseService: ObjectBaseService,
    private sharedObjectBaseService: SharedObjectBaseService,
    private publicLinkService: PublicLinkService,
    private backendService: BackendService
  ) { }

  createPublicUuid(objectToShare) {
    return this.publicLinkService.uuid().pipe(
      mergeMap(res => {
        objectToShare.publicUuid = res['uuid']
        return this.objectBaseService.update(objectToShare)
      })
    )
  }

  createPublicLink(permissions, objectToShare, type) {
    let payload = {
      ...permissions,
      [`${type}_id`]: objectToShare.id,
    };

    const redirectUrl = this.generateRedirectUrl(objectToShare);
    return this.publicLinkService.create(payload, objectToShare.publicUuid, redirectUrl);
  }

  loadPublicLink(publicUuid) {
    return this.publicLinkService.load(publicUuid)
      .pipe(
        map(res => {
          if (res) {
            const shareUrl = this.generatePublicShareUrl(publicUuid)
            const permissions = res && res['payload']
            return {
              shareUrl: shareUrl,
              permissions: permissions,
              public: true,
              passwordProtected: res?.['password_protected'] || false,
            };
          }
        }, () => {
          return {shareUrl: '', permissions: {}, public: false}
        })
      )
  }

  deletePublicLink(publicUuid) {
    return this.publicLinkService.delete(publicUuid)
  }

  createSharedObject(id, permissions, usersAndSuites, note, skip) {
    return this.sharedObjectBaseService.create(id, permissions, usersAndSuites, note, skip);
  }

  updateSharedObject(shareUuid, usersAndSuites, note, skip) {
    return this.sharedObjectBaseService.add(shareUuid, usersAndSuites, note, skip);
  }

  getSharedObject(shareUuid) {
    return this.sharedObjectBaseService.sharedObject(shareUuid).pipe(catchError(() => of(null)))
  }

  removeUserFromSharedObject(shareUuid, userOrSuiteId) {
    return this.sharedObjectBaseService.remove(shareUuid, userOrSuiteId);
  }

  updateSnapshotPermissions(objectToShare, sharingPermissions) {
    const transformedPermissions = {
      overview: sharingPermissions.views?.overview,
      breakdown: sharingPermissions.views?.breakdown,
      comparisons: sharingPermissions.comparisons,
      breakdown_cost_based_metrics: sharingPermissions.breakdownCostBasedMetrics,
      breakdown_viewability_metrics: sharingPermissions.breakdownViewabilityMetrics,
      excel_exports: sharingPermissions.excelExports,
      datasets: sharingPermissions.datasets,
    };

    return this.backendService.update(
      `snapshots/${objectToShare.id}`,
      { sharing_permissions: transformedPermissions },
    ).subscribe();
  }

  updatePermissions(isPublic, publicUuid, nonPublicSharedObject, permissions) {
    if (isPublic) {
      this.publicLinkService
      .update(publicUuid, permissions)
      .subscribe(res => { })
    }
    if (nonPublicSharedObject) {
      this.sharedObjectBaseService
      .update(nonPublicSharedObject.uuid, permissions)
      .subscribe(res => { })
    }
  }

  public savePassword(publicUuid: string, payload: {}, password: string | null): Observable<{}> {
    return this.publicLinkService.update(publicUuid, payload, password);
  }

  getAppUsers() {
    return this.objectBaseService.appUsers().pipe(
      take(1),
      map((users: any) => {
        return sortBy(users.map(user => {
          return {
            id: user.id,
            label: `${user.name} (${user.email})`,
            name: user.name,
            email: user.email,
            value: user.id,
            type: 'user',
            image: '/assets/svg/user-solid.svg',
            class: 'user-icon'
          }
        }), 'name')
      })
    );
  }

  getAppSuites() {
    return this.objectBaseService.appSuites().pipe(
      take(1),
      map((suites: any) => {
        return sortBy(suites.map(suite => {
          return {
            id: suite.id,
            label: suite.name,
            name: suite.name,
            value: suite.id,
            type: 'suite',
            image: '/assets/svg/user-group-solid.svg',
            class: 'user-icon'
          }
        }), 'name');
      })
    );
  }

  generatePublicShareUrl(publicUuid) {
    return [location.origin, 's', publicUuid].join('/')
  }

  generateShareUrl(shareUuid) {
    return [location.origin, 'sd', shareUuid].join('/')
  }

  public loadEditors(app, objectId: string): Observable<string[]> {
    return this.backendService.load(`/${app}/${objectId}/editors`);
  }

  public addEditor(app, objectId: string, editorId: string): Observable<string[]> {
    return this.backendService.update(`/${app}/${objectId}/editors/${editorId}`, {});
  }

  public removeEditor(app, objectId: string, editorId: string): Observable<string[]> {
    return this.backendService.delete(`/${app}/${objectId}/editors/${editorId}`);
  }

  private generateRedirectUrl(objectToShare) {
    return ['/s', objectToShare.publicUuid].join('/')
  }
}
